import { useForm, usePage } from '@inertiajs/vue3';
import { computed } from 'vue';

/**
 * Secure Form Composable
 *
 * This composable extends Inertia's useForm to automatically include
 * company context validation token (_company_id) in all form submissions.
 *
 * This prevents cross-tenant data contamination when:
 * 1. User opens Company A in Tab 1, starts filling form
 * 2. User switches to Company B in Tab 2 (changes session)
 * 3. User submits form in Tab 1 (still shows Company A UI)
 * 4. Backend detects company_id mismatch and blocks the request
 *
 * Usage:
 * Instead of using `useForm` from Inertia, use `useSecureForm`:
 *
 * Before:
 *   const form = useForm({ title: '', description: '' });
 *
 * After:
 *   const form = useSecureForm({ title: '', description: '' });
 *
 * The _company_id is automatically included in all submissions.
 */
export function useSecureForm(data = {}) {
    const page = usePage();

    // Get the current company_id from page props
    const companyId = computed(() => page.props.company_id);

    // Create the form with company_id automatically included
    const form = useForm({
        ...data,
        _company_id: companyId.value,
    });

    // Store the original submit methods
    const originalPost = form.post.bind(form);
    const originalPut = form.put.bind(form);
    const originalPatch = form.patch.bind(form);
    const originalDelete = form.delete.bind(form);

    // Override submit methods to ensure _company_id is always current
    form.post = function(url, options = {}) {
        // Update company_id before submission in case it changed
        form._company_id = companyId.value;
        return originalPost(url, options);
    };

    form.put = function(url, options = {}) {
        form._company_id = companyId.value;
        return originalPut(url, options);
    };

    form.patch = function(url, options = {}) {
        form._company_id = companyId.value;
        return originalPatch(url, options);
    };

    form.delete = function(url, options = {}) {
        form._company_id = companyId.value;
        return originalDelete(url, options);
    };

    return form;
}
